import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { designColors, designShell } from "services/constants/designTokens";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { approvePanelUi, useGoToApprovalCenter } from "../approveUi";
import { reviewClaims, reviewOutcome, useReimbursementBillUpload } from "pages/PayRoll/Shared/reimbursementApi";
import {
  ClaimChecksBox,
  ClaimRequestBody,
  buildClaimChecks,
} from "pages/PayRoll/Shared/reimbursementClaimUi";

// A single reason dialog for Reject (Pending) and Cancel (Approved).
const ReasonDialog = ({ open, title, intro, confirmLabel, cancelLabel, placeholder, busy, onCancel, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setReason("");
      setError("");
    }
  }, [open]);
  return (
    <GlobalConfirmDialog
      open={open}
      title={title}
      onCancel={() => { if (!busy) onCancel(); }}
      onConfirm={() => {
        if (busy) return undefined;
        const trimmed = reason.trim();
        if (!trimmed) return setError("Reason is required");
        if (trimmed.length < 3) return setError(minimum3Characters);
        return onConfirm(trimmed);
      }}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      danger
      reasonValue={reason}
      onReasonChange={(value) => {
        setReason(value.slice(0, 255));
        if (error) setError("");
      }}
      reasonError={error}
      reasonPlaceholder={placeholder}
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>{intro}</Mui.Typography>
    </GlobalConfirmDialog>
  );
};

// Team Workspace > Approvals > Reimbursement view panel, laid out like the
// other Approvals view panels (e.g. Off-Day Duty): employee card with
// status, the limit / bill-age checks while Pending, a Request Details card
// and the shared status stepper (Paid in salary shows as Completed).
// Approve / Reject for Pending claims and Cancel for Approved ones go
// through the team review API; the server only changes Pending claims
// today, so Cancel on an Approved claim shows its "skipped" answer.
// No Edit here: the team API only changes a claim's status, it can't edit
// its fields yet (My Workspace keeps Edit for the employee's own claims).
export const ExpenseDetailPanel = ({ open, onClose, row, claims = [], types = [], domain, companyId, onChanged, expanded = false, onToggleExpand }) => {
  const { userScreenAccess } = useSelector((state) => state.tokenReducer);
  const goToApprovalCenter = useGoToApprovalCenter();
  const [dialog, setDialog] = useState(null); // "reject" | "cancel" | null
  const [busy, setBusy] = useState(false);
  const billUploadEnabled = useReimbursementBillUpload(domain, companyId);

  const hasApproverAccess =
    checkFirstLevelApproverPermission(userScreenAccess?.module_details, "Approvals", "Expense") ||
    checkSecondLevelApproverPermission(userScreenAccess?.module_details, "Approvals", "Expense");

  const rowId = row?.id;
  useEffect(() => setDialog(null), [rowId]);

  if (!row) return null;

  const denied = (action) => showToast("error", `Access denied: You are not authorized to ${action} this reimbursement request`);

  const review = async (status, verb, reason) => {
    setBusy(true);
    try {
      const result = await reviewClaims({ domain, companyId, ids: [row.id], status, reason });
      const outcome = reviewOutcome(result, verb);
      if (outcome.type === "error") {
        showToast("error", status === "Cancelled"
          ? `${row.requestId} could not be cancelled. The server only changes pending claims for now.`
          : outcome.text);
        return;
      }
      showToast("success", `${row.requestId} ${verb}.`);
      setDialog(null);
      onChanged?.();
      onClose?.();
    } catch (error) {
      showToast("error", error.message || "Couldn't update the claim. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = () => {
    if (busy) return undefined;
    if (!hasApproverAccess) return denied("approve");
    return review("Approved", "approved");
  };
  const handleReject = (reason) => {
    if (!hasApproverAccess) return denied("reject");
    return review("Rejected", "rejected", reason);
  };
  const handleCancel = (reason) => {
    if (!hasApproverAccess) return denied("cancel");
    return review("Cancelled", "cancelled", reason);
  };

  const type = types.find((item) => item.id === row.typeId) || null;
  const checks =
    row.status === "Pending"
      ? buildClaimChecks({
          type,
          amount: row.amount,
          billDate: row.billDate,
          claims,
          employeeId: row.userId,
          excludeId: row.id,
        })
      : [];

  const footer =
    row.status === "Pending" && hasApproverAccess ? (
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: "100%" }}>
        <Mui.Button
          variant="outlined"
          disabled={busy}
          startIcon={<HighlightOffIcon sx={{ fontSize: 17 }} />}
          onClick={() => setDialog("reject")}
          sx={{
            ...designShell.panelActionButton,
            flex: 1,
            minHeight: 42,
            color: "#E11D48",
            borderColor: "#FDA4AF",
            backgroundColor: designColors.white,
            "&:hover": { color: "#BE123C", borderColor: "#FB7185", backgroundColor: "#FFF1F3" },
          }}
        >
          Reject
        </Mui.Button>
        <PrimaryButton disabled={busy} startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 17 }} />} onClick={handleApprove} sx={{ flex: 1, minHeight: 42 }}>
          {busy ? "Saving..." : "Approve"}
        </PrimaryButton>
      </Mui.Stack>
    ) : row.status === "Approved" && hasApproverAccess ? (
      <Mui.Stack spacing={1.5} sx={{ width: "100%" }}>
        <Mui.Typography sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}>
          This claim is approved and waiting for payroll.
        </Mui.Typography>
        <RequestCancelButton disabled={busy} onClick={() => setDialog("cancel")}>Cancel claim</RequestCancelButton>
      </Mui.Stack>
    ) : null;

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={onClose}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="Reimbursement Request"
        subtitle="Request details and status"
        headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        PaperProps={{
          sx: {
            backgroundColor: designColors.white,
            maxWidth: "92vw",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
          },
        }}
        breadcrumb={[
          { label: "Approval Center", onClick: goToApprovalCenter },
          { label: "Reimbursement", onClick: onClose },
          row.employee?.name || row.requestId,
        ]}
        footer={footer}
      >
        <ClaimRequestBody row={row} employee={row.employee} type={type} billUploadEnabled={billUploadEnabled}>
          <ClaimChecksBox title="Checks" rows={checks} failHint="Check this before approving." />
        </ClaimRequestBody>
      </GlobalSidePanel>
      <ReasonDialog
        open={dialog === "reject"}
        title="Reject Reimbursement Request"
        intro="Enter the reason for rejecting this claim. The employee will see it."
        confirmLabel="Reject"
        cancelLabel="Cancel"
        placeholder="Provide a reason for rejection"
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={handleReject}
      />
      <ReasonDialog
        open={dialog === "cancel"}
        title="Cancel Reimbursement Claim"
        intro="This claim is already approved. Enter a reason to cancel it."
        confirmLabel="Cancel Claim"
        cancelLabel="Keep Claim"
        placeholder="Provide a reason for cancelling this approved claim"
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={handleCancel}
      />
    </>
  );
};
